<template>
  <section>
    <b-modal :active.sync="isModalActive" has-modal-card :can-cancel="['button']" trap-focus
      custom-class="centered-modal">

      <div class="modal-card" style="width: 1100px; max-width: 95vw;">

        <header class="modal-card-head">
          <div class="flex items-center gap-4 w-full">
            <b-icon pack="fas" icon="cube" size="is-large" type="is-info"></b-icon>
            <div class="flex-1">
              <p class="modal-card-title m-0 has-text-weight-bold">{{ $t('select_ifc') }}</p>
              <p class="is-size-7 has-text-grey mt-1">{{ $t('select_ifc') }}</p>
            </div>
            <button class="delete is-large" aria-label="close" @click="hide"></button>
          </div>
        </header>

        <section class="modal-card-body" style="min-height: 450px; max-height: 70vh;">

          <b-loading :is-full-page="false" v-model="isLoading" :can-cancel="false"></b-loading>

          <b-tabs v-model="activeTab" type="is-boxed" expanded v-show="!isLoading" class="mt-2">
            <b-tab-item>
              <template slot="header">
                <b-icon pack="fas" icon="file-alt" size="is-small"></b-icon>
                <span>{{ $t('individual_models') }}</span>
              </template>

              <div v-if="individualDocuments.length === 0" class="has-text-centered py-6 has-text-grey-light">
                <b-icon pack="fas" icon="folder-open" size="is-large" class="mb-3"></b-icon>
                <p class="has-text-weight-semibold">{{ $t('no_documents') }}</p>
              </div>

              <div v-else class="flex flex-col gap-2 mt-4">
                <div v-for="doc in individualDocuments" :key="doc.id" :title="doc.name" @click="loadIndividual(doc)"
                  class="box p-3 m-0 cursor-pointer hover-box transition-colors">

                  <div class="flex items-center w-full">

                    <b-icon pack="fas" icon="cube" size="is-small"
                      class="has-text-grey-light flex-shrink-0 mr-3"></b-icon>

                    <span class="is-size-7 has-text-weight-bold has-text-dark flex-1 min-w-0 truncate">
                      {{ doc.name }}
                    </span>

                    <b-icon pack="fas" icon="arrow-right" size="is-small" type="is-info"
                      class="arrow-icon flex-shrink-0 ml-3"></b-icon>

                  </div>

                </div>
              </div>
            </b-tab-item>
            <b-tab-item>
              <template slot="header">
                <b-icon pack="fas" icon="layer-group" size="is-small"></b-icon>
                <span>{{ $t('federated_models') }}</span>
              </template>

              <div v-if="isLoadingFederated" class="has-text-centered py-6">
                <b-icon pack="fas" icon="spinner" custom-class="fa-pulse" type="is-info" size="is-large"></b-icon>
              </div>

              <div v-else-if="federatedGroups.length === 0" class="has-text-centered py-6 has-text-grey-light">
                <b-icon pack="fas" icon="folder-open" size="is-large" class="mb-3"></b-icon>
                <p class="has-text-weight-semibold">{{ $t('no_documents') }}</p>
              </div>

              <div v-else class="mt-4">
                <div v-for="group in federatedGroups" :key="group.federation_id" class="card mb-4 shadow-sm">

                  <header class="card-header cursor-pointer" @click="toggleGroup(group)"
                    :class="{ 'opacity-50': !hasReadyXkts(group) }">
                    <p class="card-header-title is-size-6 m-0">
                      <b-icon pack="fas" :icon="isGroupExpanded(group) ? 'chevron-down' : 'chevron-right'"
                        class="mr-3 has-text-grey"></b-icon>
                      <span class="has-text-weight-bold">{{ group.federation_name }}</span>

                      <span class="has-text-grey has-text-weight-normal is-size-7 ml-3">
                        <b-icon pack="fas" icon="cubes" size="is-small" class="mr-1"></b-icon>
                        {{ readyXktsCount(group) }} {{ $t('of') }} {{ group.ifc_documents.length }}
                      </span>
                    </p>
                    <div class="card-header-icon" v-if="!hasReadyXkts(group)">
                      <b-tag type="is-danger is-light" class="has-text-weight-bold">{{ $t('pending') }}</b-tag>
                    </div>
                  </header>

                  <div class="card-content has-background-white-ter p-4" v-if="isGroupExpanded(group)">

                    <div class="level box p-3 mb-4">
                      <div class="level-left">
                        <div class="level-item">
                          <b-switch v-model="federatedSuperimpose" type="is-info" size="is-small">
                            <span class="has-text-weight-semibold is-size-7">
                              {{ federatedSuperimpose ? $t('ifc_same_building') : $t('ifc_different_buildings') }}
                            </span>
                          </b-switch>
                        </div>
                      </div>
                      <div class="level-right">
                        <div class="level-item">
                          <b-button type="is-info" size="is-small" icon-pack="fas" icon-left="play"
                            class="has-text-weight-bold" :disabled="!hasReadyXkts(group)"
                            @click="loadGroupFederated(group)">
                            {{ $t('load_complete_group') }}
                          </b-button>
                        </div>
                      </div>
                    </div>

                    <p class="is-size-7 has-text-grey has-text-weight-bold is-uppercase mb-3">
                      {{ $t('or_select_individual_file') }}:
                    </p>

                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div v-for="doc in group.ifc_documents" :key="doc.id" :title="doc.name"
                        @click="loadIndividual(doc)" class="box p-2.5 m-0 flex items-center transition-colors"
                        :class="doc.xktpath ? 'cursor-pointer hover-box' : 'opacity-50 cursor-not-allowed has-background-light'">

                        <div class="flex items-center w-full">

                          <b-icon pack="fas" :icon="doc.xktpath ? 'cube' : 'hourglass-half'" size="is-small"
                            :type="doc.xktpath ? 'is-info' : ''"
                            class="has-text-grey-light flex-shrink-0 mr-2"></b-icon>

                          <span
                            class="is-size-7 has-text-weight-bold flex-1 min-w-0 line-clamp-2 break-words whitespace-normal leading-snug"
                            :class="doc.xktpath ? 'has-text-dark' : 'has-text-grey'">
                            {{ doc.name }}
                          </span>

                          <b-icon v-if="doc.xktpath" pack="fas" icon="arrow-right" size="is-small" type="is-info"
                            class="arrow-icon flex-shrink-0 ml-2"></b-icon>

                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </b-tab-item>
          </b-tabs>

        </section>

        <footer class="modal-card-foot" style="justify-content: flex-end;">
          <b-button @click="hide" size="is-small" class="has-text-weight-bold px-4">
            {{ $t('close') }}
          </b-button>
        </footer>

      </div>
    </b-modal>
  </section>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ifc-document-selector-modal',

  props: {
    projectId: { type: [Number, String], required: true }
  },

  data() {
    return {
      isModalActive: false,
      isLoading: false,
      isLoadingFederated: false,
      activeTab: 0,
      individualDocuments: [],
      federatedGroups: [],
      expandedGroupIds: [],
      federatedSuperimpose: false,
    }
  },

  methods: {
    show() {
      this.isModalActive = true
      this.activeTab = 0
      this.fetchDocuments()
      this.fetchFederatedDocuments()
    },

    hide() {
      this.isModalActive = false
      this.individualDocuments = []
      this.federatedGroups = []
      this.expandedGroupIds = []
    },

    async fetchDocuments() {
      this.isLoading = true
      this.individualDocuments = []
      try {
        const resp = await axios.get(`/api/v2/project/${this.projectId}/ifc-documents`)
        const list = (resp.data && resp.data.data) ? resp.data.data : []
        this.individualDocuments = list.filter(d => d && d.xktpath && !d.federation_id)
      } catch (err) {
        this.$notify.error((err.response?.data?.msg) || err.message || 'Error')
      } finally {
        this.isLoading = false
      }
    },

    async fetchFederatedDocuments() {
      this.isLoadingFederated = true
      this.federatedGroups = []
      try {
        const resp = await axios.get(`/api/v2/project/${this.projectId}/ifc-federated-documents`)
        this.federatedGroups = (resp.data && resp.data.data) ? resp.data.data : []
      } catch (err) {
        this.$notify.error((err.response?.data?.msg) || err.message || 'Error')
      } finally {
        this.isLoadingFederated = false
      }
    },

    loadIndividual(doc) {
      if (!doc.xktpath) return
      // Emitir objeto con url y documentId para que el visor pueda enviar avances por elemento
      this.$emit('document-selected', { url: doc.xktpath, documentId: doc.id })
      this.hide()
    },

    loadGroupFederated(group) {
      if (!this.hasReadyXkts(group)) return
      const ready = group.ifc_documents.filter(d => d.xktpath)
      const xktpaths = ready.map(d => d.xktpath)
      const documentIds = ready.map(d => d.id)

      this.$emit('document-selected', { urls: xktpaths, federated: this.federatedSuperimpose, documentIds })
      this.hide()
    },

    toggleGroup(group) {
      const idx = this.expandedGroupIds.indexOf(group.federation_id)
      if (idx >= 0) {
        this.expandedGroupIds.splice(idx, 1)
      } else {
        this.expandedGroupIds.push(group.federation_id)
      }
    },

    isGroupExpanded(group) {
      return this.expandedGroupIds.includes(group.federation_id)
    },

    hasReadyXkts(group) {
      return group.ifc_documents.some(d => d.xktpath)
    },

    readyXktsCount(group) {
      return group.ifc_documents.filter(d => d.xktpath).length
    }
  }
}
</script>

<style>
.centered-modal .animation-content {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  width: 100% !important;
  max-width: 100% !important;
}

.centered-modal .modal-card {
  margin: 0 auto !important;
}

.centered-modal.modal .modal-background + .animation-content {
  max-width: 100% !important;
}
</style>

<style scoped>
.hover-box {
  transition: all 0.2s ease-in-out;
  border: 1px solid transparent;
}

.hover-box:hover {
  border-color: #3273dc;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.hover-box .arrow-icon {
  opacity: 0;
  transition: opacity 0.2s;
}

.hover-box:hover .arrow-icon {
  opacity: 1;
}
</style>